import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { type FilterDescriptor } from '../../../lib/resource-list/filters.js';
import { pmsCredentials } from '../api/endpoints.js';
import { useSoftDeletePmsCredential } from '../api/mutations.js';
import {
  PMS_PROVIDER_OPTIONS,
  PMS_STATUS_OPTIONS,
  type PmsCredentialRow,
  pmsCredentialRow,
} from '../api/schemas.js';

const filters: readonly FilterDescriptor[] = [
  {
    id: 'status',
    label: 'Status',
    param: 'status',
    kind: 'select',
    options: PMS_STATUS_OPTIONS,
  },
  {
    id: 'pmsProvider',
    label: 'PMS type',
    param: 'pmsProvider',
    kind: 'select',
    options: PMS_PROVIDER_OPTIONS,
  },
];

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export const pmsCredentialColumns: readonly ColumnDescriptor<PmsCredentialRow>[] = [
  {
    id: 'name',
    header: 'Connection',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'pmsProvider'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/pms-credentials/${row.id}`}
        secondary={row.pmsProvider ?? null}
      />
    ),
  },
  {
    id: 'pmsProvider',
    header: 'PMS type',
    sortable: true,
    fields: ['pmsProvider'],
    cell: (row) => row.pmsProvider,
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['status'],
    cell: (row) =>
      row.status === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>
      ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    defaultVisible: false,
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: PmsCredentialRow): React.JSX.Element => <PmsCredentialRowActions row={row} />,
  },
];

function PmsCredentialRowActions({ row }: { readonly row: PmsCredentialRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeletePmsCredential();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/pms-credentials/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/pms-credentials/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.PMS_CONFIGURE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete PMS credential"
        description={`${label} will be removed from this list. Its history is retained, not erased.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export function PmsCredentialListPage(): React.JSX.Element {
  return (
    <ResourceListPage
      title="PMS credentials"
      description="Every PMS connection this account manages."
      path={pmsCredentials.endpoints.list}
      resource="pmsCredentials"
      schema={pmsCredentialRow}
      columns={pmsCredentialColumns}
      filters={filters}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.PMS_READ}
      actions={
        <PermissionGuard permission={PERMISSIONS.PMS_CONFIGURE}>
          <Button asChild>
            <Link to="/pms-credentials/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New credential
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
